import { useState, useRef, useEffect } from "react";
import { X, Send, Loader2, Bot } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Ciao! Sono l'assistente virtuale di Metodo Contabile. Come posso aiutarti oggi? Puoi chiedermi informazioni su tasse, fisco, partita IVA e molto altro!",
    },
  ]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const sendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = inputValue.trim();
    setInputValue("");
    setMessages((prev) => [...prev, { role: "user", content: userMessage }]);
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/chat`, {
        message: userMessage,
        session_id: sessionId,
      });

      setSessionId(response.data.session_id);
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: response.data.response },
      ]);
    } catch (error) {
      console.error("Chat error:", error);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content:
            "Mi dispiace, si è verificato un errore. Ti consiglio di contattare direttamente lo studio per assistenza.",
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Chat Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-44 right-6 z-[9999] bg-white hover:bg-gray-50 text-brand-primary p-4 rounded-full shadow-lg border-2 border-brand-primary chatbot-bubble transition-transform hover:scale-110"
          data-testid="chatbot-open-btn"
          aria-label="Apri chat AI"
        >
          <Bot size={28} />
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div
          className="fixed bottom-44 right-6 z-[9999] w-[360px] max-w-[calc(100vw-48px)] h-[500px] max-h-[calc(100vh-200px)] bg-white rounded-2xl shadow-2xl border border-brand-border flex flex-col animate-slide-up"
          data-testid="chatbot-window"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-brand-border bg-brand-primary rounded-t-2xl">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <Bot size={20} className="text-white" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-white">
                  Assistente AI
                </h3>
                <p className="text-white/70 text-xs">Metodo Contabile</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="p-2 text-white/70 hover:text-white transition-colors"
              data-testid="chatbot-close-btn"
              aria-label="Chiudi chat"
            >
              <X size={20} />
            </button>
          </div>

          {/* Messages */}
          <ScrollArea className="flex-1 p-4">
            <div className="space-y-4">
              {messages.map((msg, index) => (
                <div
                  key={index}
                  className={`flex ${
                    msg.role === "user" ? "justify-end" : "justify-start"
                  }`}
                >
                  <div
                    className={`max-w-[80%] p-3 rounded-2xl text-sm ${
                      msg.role === "user"
                        ? "bg-brand-primary text-white rounded-br-md"
                        : "bg-brand-secondary text-brand-foreground rounded-bl-md"
                    }`}
                    data-testid={`chat-message-${index}`}
                  >
                    {msg.content}
                  </div>
                </div>
              ))}
              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-brand-secondary p-3 rounded-2xl rounded-bl-md">
                    <Loader2
                      size={20}
                      className="animate-spin text-brand-muted"
                    />
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </ScrollArea>

          {/* Input */}
          <div className="p-4 border-t border-brand-border">
            <div className="flex gap-2">
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Scrivi un messaggio..."
                className="flex-1 px-4 py-3 bg-brand-secondary rounded-full text-sm text-brand-foreground placeholder:text-brand-muted focus:outline-none focus:ring-2 focus:ring-brand-primary"
                data-testid="chatbot-input"
                disabled={isLoading}
              />
              <Button
                onClick={sendMessage}
                disabled={!inputValue.trim() || isLoading}
                className="bg-brand-primary hover:bg-brand-primary-dark text-white rounded-full p-3 h-auto"
                data-testid="chatbot-send-btn"
              >
                <Send size={18} />
              </Button>
            </div>
            <p className="text-xs text-brand-muted text-center mt-2">
              Powered By{" "}
              <a
                href="https://telcavoip.ai"
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand-primary hover:underline"
              >
                TelcaVoIP AI
              </a>{" "}
              - Non sostituisce la consulenza professionale che puoi ottenere contattando Metodo Contabile
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default Chatbot;
